import type { KioskState, PunchOutcome } from './domain.js';
import { isCurrentEmployee, parisDayIsoRange, todayIsoParis } from './domain.js';
import type { ClockStore, EmployeeStore } from './ports.js';

// ── Clock policy constants (S3 — ADR-0009 §5) ───────────────────────────────
//
// The kiosk punch is a TOGGLE: no open entry → clock in; open entry → clock
// out. Two guard rails keep forgotten punches from corrupting the record:
//
//  * A stale open shift (opened more than MAX_OPEN_SHIFT_HOURS ago — someone
//    forgot to clock out) is NOT closed by the next punch: closing it would
//    record a giant bogus shift. It stays open for the operator's monthly
//    correction pass (exactly today's Connecteam amendment practice), and the
//    punch opens a fresh entry instead.
//  * The kiosk may submit a client timestamp (`atIso`) so its offline retry
//    queue preserves the real punch time. It is trusted only within a sane
//    window; anything outside collapses to server time.

/** An open shift older than this is "forgotten", left for operator correction. */
export const MAX_OPEN_SHIFT_HOURS = 14;
/** Client punch timestamps may not be in the future beyond clock skew. */
export const PUNCH_FUTURE_TOLERANCE_MS = 5 * 60_000;
/** Client punch timestamps older than this (offline queue) collapse to now. */
export const PUNCH_PAST_TOLERANCE_MS = 12 * 3_600_000;
/** A clock-out is never recorded at/before its clock-in (DB check constraint). */
const MIN_SHIFT_MS = 60_000;

export interface PunchInput {
  employeeId: string;
  pin: string;
  /** Optional kiosk-side punch time (offline retry) — clamped, see above. */
  atIso?: string;
}

/**
 * TDA orchestrator for the reception kiosk (S3): PIN check, toggle punch,
 * stale-open-shift handling. Unit-tested against fakes; the /api/staff/clock
 * edge function is a thin shell over it.
 */
export class ClockService {
  constructor(
    private readonly employees: EmployeeStore,
    private readonly clock: ClockStore,
  ) {}

  async kioskState(now: Date = new Date()): Promise<KioskState> {
    const { fromIso, toIso } = parisDayIsoRange(todayIsoParis(now));
    const [employees, trustedPersons, openEntries, todaysEntries] = await Promise.all([
      this.employees.listEmployees(),
      this.employees.listTrustedPersons(),
      this.clock.listOpenEntries(),
      this.clock.listEntriesBetween(fromIso, toIso),
    ]);
    // Departed employees drop off the punch grid (0031 scoping); their past
    // entries are untouched — corrections/reports live on the back-office.
    return {
      employees: employees.filter((e) => isCurrentEmployee(e)),
      trustedPersons,
      openEntries,
      todaysEntries,
    };
  }

  async punch(input: PunchInput, nowIso: string = new Date().toISOString()): Promise<PunchOutcome> {
    const pinCheck = await this.employees.verifyPin(input.employeeId, input.pin);
    if (pinCheck !== 'ok') return { kind: 'rejected', reason: pinCheck };

    const now = Date.parse(nowIso);
    let at = input.atIso ? Date.parse(input.atIso) : Number.NaN;
    if (!Number.isFinite(at) || at > now + PUNCH_FUTURE_TOLERANCE_MS || at < now - PUNCH_PAST_TOLERANCE_MS) {
      at = now;
    }

    const open = await this.clock.findOpenEntry(input.employeeId);
    if (!open) {
      const entry = await this.clock.openEntry(input.employeeId, new Date(at).toISOString());
      return { kind: 'in', entry, staleOpenEntryId: null };
    }

    const openedAt = Date.parse(open.clockIn);
    if (at - openedAt > MAX_OPEN_SHIFT_HOURS * 3_600_000) {
      const entry = await this.clock.openEntry(input.employeeId, new Date(at).toISOString());
      return { kind: 'in', entry, staleOpenEntryId: open.id };
    }

    const closeAt = Math.max(at, openedAt + MIN_SHIFT_MS);
    const entry = await this.clock.closeEntry(open.id, new Date(closeAt).toISOString());
    return { kind: 'out', entry };
  }
}
